<script setup lang="ts">
/**
 * CampaignHeroImage — carousel d'images "desktop/card" arrondi avec badge
 * vérifié optionnel + flèches de navigation + indicateurs.
 *
 * Composant PARTAGÉ (@donoor/ui) consommé en SOURCE par dashboard.donoor.org
 * et donoor.org. Couleurs en hex en dur (pas de token de thème) → rendu
 * identique dans les 2 apps.
 *
 * `<UIcon>` reste global (résolu par l'app Nuxt qui transpile ce SFC via
 * build.transpile).
 *
 * USAGE (auto-import via wrapper local) :
 *   import CampaignHeroImage from "@donoor/ui/campaign/CampaignHeroImage.vue";
 *
 * Karim 2026-06-21 (factorisation cagnotte).
 */
// Internationalisation (lot "paquets partagés", 2026-10-11) : clés `ui.*`,
// présentes dans donoor.org ET dashboard.donoor.org (useI18n auto-importé).
const { t } = useI18n();

const props = defineProps<{
  images: { id: string; url: string }[];
  isVerified?: boolean;
}>();

const currentIndex = ref(0);

const hasMultiple = computed(() => props.images.length > 1);

function prev() {
  currentIndex.value =
    currentIndex.value === 0 ? props.images.length - 1 : currentIndex.value - 1;
}
function next() {
  currentIndex.value =
    currentIndex.value === props.images.length - 1 ? 0 : currentIndex.value + 1;
}
function goTo(index: number) {
  currentIndex.value = index;
}
</script>

<template>
  <!-- SANS IMAGE : rien du tout plutôt qu'un cadre gris vide (Karim
       2026-10-08). Le badge « vérifiée » reste affiché par
       CampaignSecurityBadge sous le titre. -->
  <div
    v-if="images.length > 0"
    class="relative w-full aspect-16/9 rounded-3xl overflow-hidden bg-gray-100"
  >
    <div
      v-for="(image, index) in images"
      :key="image.id"
      class="absolute inset-0 transition-opacity duration-300"
      :class="index === currentIndex ? 'opacity-100' : 'opacity-0 pointer-events-none'"
    >
      <!-- object-cover : l'image remplit tout le cadre, recadrée au centre
           (comme Airbnb/HelloAsso/Leetchi). Cadre 3/2 (moins plat que 7/4)
           → coupe minimale quel que soit le ratio uploadé, rendu net et plein,
           zéro bande. -->
      <img
        :src="image.url"
        :alt="t('ui.heroImage.imageAlt', { numero: index + 1 })"
        :loading="index === 0 ? 'eager' : 'lazy'"
        :fetchpriority="index === 0 ? 'high' : 'auto'"
        :decoding="index === 0 ? 'sync' : 'async'"
        class="absolute inset-0 w-full h-full object-cover"
      />
    </div>

    <div v-if="isVerified" class="absolute top-4 right-4 z-10">
      <UIcon
        name="i-lucide-badge-check"
        class="size-8 text-[#1f9afe] drop-shadow-lg"
      />
    </div>

    <template v-if="hasMultiple">
      <button
        type="button"
        class="absolute left-3 bottom-3 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center transition-colors backdrop-blur-sm"
        :aria-label="t('ui.heroImage.imagePrecedente')"
        @click="prev"
      >
        <UIcon name="i-lucide-arrow-left" class="size-4" />
      </button>
      <button
        type="button"
        class="absolute left-14 bottom-3 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center transition-colors backdrop-blur-sm"
        :aria-label="t('ui.heroImage.imageSuivante')"
        @click="next"
      >
        <UIcon name="i-lucide-arrow-right" class="size-4" />
      </button>

      <div class="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
        <button
          v-for="(image, index) in images"
          :key="image.id"
          type="button"
          class="h-1.5 rounded-full transition-all"
          :class="index === currentIndex ? 'w-6 bg-white' : 'w-1.5 bg-white/60 hover:bg-white/80'"
          :aria-label="t('ui.heroImage.allerALImage', { numero: index + 1 })"
          @click="goTo(index)"
        />
      </div>
    </template>
  </div>
</template>
